import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useClient } from "../../contexts/ClientContext";
import { LOCAL_STORAGE_DEMO_PRIVATE_KEY } from "../constants";

const SecureRoute = ({
  children,
  requiredPermission = null,
  allowLegacyPrivateKeyFallback = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clientInfo, isLoading, hasPermission } = useClient();
  const [inputKey, setInputKey] = useState("");
  const [showPrivateKey, setShowPrivateKey] = useState(false);

  useEffect(() => {
    if (!allowLegacyPrivateKeyFallback || isLoading || clientInfo) {
      return;
    }

    const storedKey =
      localStorage.getItem(LOCAL_STORAGE_DEMO_PRIVATE_KEY) || "";
    setInputKey(storedKey);
  }, [allowLegacyPrivateKeyFallback, clientInfo, isLoading]);

  if (isLoading) {
    return null;
  }

  if (!clientInfo) {
    if (!allowLegacyPrivateKeyFallback) {
      return <Navigate to="/sign-in" replace />;
    }

    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={10}>
        <Typography variant="h6" gutterBottom>
          {t("auth.legacyPrivateKeyTitle")}
        </Typography>
        <TextField
          label={t("auth.privateKeyLabel")}
          type={showPrivateKey ? "text" : "password"}
          variant="outlined"
          value={inputKey}
          onChange={(e) => {
            setInputKey(e.target.value);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t("auth.togglePrivateKeyVisibility")}
                  onClick={() => setShowPrivateKey((prev) => !prev)}
                  edge="end"
                >
                  {showPrivateKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2, width: "300px" }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("auth.legacyPrivateKeyDescription")}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/sign-in")}>
          {t("auth.goToSignIn")}
        </Button>
      </Box>
    );
  }

  // Check if user has required permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" mt={10}>
        <Typography variant="h6" color="error">
          {t("auth.accessDenied")}
        </Typography>
        <Typography>{t("auth.noPermission")}</Typography>
      </Box>
    );
  }

  return <>{children}</>;
};

export default SecureRoute;
